import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, MessageCircle, Loader2 } from "lucide-react";
import { useLang } from "../LanguageContext";
import { waLink } from "../restaurant.config";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactForm() {
  const { t, lang } = useLang();
  const f = t.contatti.form;

  const [values, setValues] = useState({
    name: "",
    email: "",
    phone: "",
    people: "",
    date: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: null }));
  };

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = f.required;
    if (!values.email.trim()) next.email = f.required;
    else if (!emailRe.test(values.email.trim())) next.email = f.invalidEmail;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildWhatsAppText = () => {
    const lines = [
      `${f.title} — Grappolo d'Oro`,
      "",
      `${f.name}: ${values.name}`,
      `${f.email}: ${values.email}`,
    ];
    if (values.phone) lines.push(`${f.phone}: ${values.phone}`);
    if (values.people) lines.push(`${f.people}: ${values.people}`);
    if (values.date) lines.push(`${f.date}: ${values.date}`);
    if (values.message) lines.push("", `${f.message}:`, values.message);
    return lines.join("\n");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, { ...values, lang }, { timeout: 15000 });
      toast.success(f.successTitle, { description: f.successBody });
      setValues({
        name: "",
        email: "",
        phone: "",
        people: "",
        date: "",
        message: "",
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Inquiry submit failed", err);
      toast.error(f.errorTitle, { description: f.errorBody });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#23211E]/30 focus:border-[#B85532] focus:outline-none py-3 text-base font-light placeholder:text-[#3D3A35]/40 transition-colors";
  const labelCls =
    "text-[10px] uppercase tracking-[0.28em] text-[#565F41] mb-2 block";

  return (
    <section
      id="richiedi-info"
      data-testid="contact-form-section"
      className="bg-[#EBE7DD] text-[#23211E] py-20 md:py-32 px-6 md:px-10 border-t border-[#23211E]/10"
    >
      <div className="max-w-[1100px] mx-auto grid grid-cols-12 gap-8 md:gap-12">
        <div className="col-span-12 md:col-span-5 reveal-on-scroll">
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#565F41] mb-6">
            {f.eyebrow}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl leading-[0.95] tracking-tight">
            {f.title}
          </h2>
          <p className="mt-6 text-base font-light leading-relaxed text-[#3D3A35] max-w-md">
            {f.intro}
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          noValidate
          className="col-span-12 md:col-span-7 grid grid-cols-2 gap-x-6 gap-y-6"
        >
          <div className="col-span-2 md:col-span-1">
            <label htmlFor="cf-name" className={labelCls}>
              {f.name} *
            </label>
            <input
              id="cf-name"
              data-testid="form-name"
              type="text"
              value={values.name}
              onChange={update("name")}
              className={inputCls}
              aria-invalid={!!errors.name}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-[#B85532]" data-testid="form-name-error">
                {errors.name}
              </p>
            )}
          </div>

          <div className="col-span-2 md:col-span-1">
            <label htmlFor="cf-email" className={labelCls}>
              {f.email} *
            </label>
            <input
              id="cf-email"
              data-testid="form-email"
              type="email"
              value={values.email}
              onChange={update("email")}
              className={inputCls}
              aria-invalid={!!errors.email}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-[#B85532]" data-testid="form-email-error">
                {errors.email}
              </p>
            )}
          </div>

          <div className="col-span-2 md:col-span-1">
            <label htmlFor="cf-phone" className={labelCls}>
              {f.phone}
            </label>
            <input
              id="cf-phone"
              data-testid="form-phone"
              type="tel"
              value={values.phone}
              onChange={update("phone")}
              className={inputCls}
            />
          </div>

          <div className="col-span-1">
            <label htmlFor="cf-people" className={labelCls}>
              {f.people}
            </label>
            <input
              id="cf-people"
              data-testid="form-people"
              type="text"
              inputMode="numeric"
              value={values.people}
              onChange={update("people")}
              className={inputCls}
              placeholder="2"
            />
          </div>

          <div className="col-span-1">
            <label htmlFor="cf-date" className={labelCls}>
              {f.date}
            </label>
            <input
              id="cf-date"
              data-testid="form-date"
              type="text"
              value={values.date}
              onChange={update("date")}
              className={inputCls}
              placeholder="21/12 · 20:00"
            />
          </div>

          <div className="col-span-2">
            <label htmlFor="cf-message" className={labelCls}>
              {f.message}
            </label>
            <textarea
              id="cf-message"
              data-testid="form-message"
              rows={4}
              value={values.message}
              onChange={update("message")}
              placeholder={f.messagePlaceholder}
              className={`${inputCls} resize-none leading-relaxed`}
            />
          </div>

          <div className="col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
            <button
              type="submit"
              data-testid="form-submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#23211E] text-[#F5F2EC] text-xs uppercase tracking-[0.22em] hover:-translate-y-0.5 hover:bg-[#B85532] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                  {f.submitting}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" strokeWidth={1.5} />
                  {f.submit}
                </>
              )}
            </button>

            <a
              data-testid="form-whatsapp-fallback"
              href={waLink(buildWhatsAppText())}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#565F41] hover:text-[#B85532] transition-colors"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
              {f.alsoWhatsapp}
            </a>
          </div>
        </form>
      </div>
    </section>
  );
}
